import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardSubtitle,
} from "reactstrap";
import noData from "../../assets/images/NoData.gif";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import DateRangeSelector from "./DateRangeSelector";
import {
  formatDateForPayload,
  getCurrentWeekRange,
} from "../Utils/UtilityFunctions";
import { getAllStationUsage } from "../../slices/EVDashboard/StationMetrics/thunk";
import { getAllStation } from "../../slices/stations/thunk";

const StationUtilizationChart = () => {
  const currentWeekRange = getCurrentWeekRange();
  const [dateRange, setDateRange] = useState(currentWeekRange);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const stationUsage =
    useSelector((state) => state.StationUsage?.stationUsage) || [];
  const stationsData = useSelector((state) => state.Stations?.station);

  // Merge stationUsage with stationsData by matching stationID
  const mergedStationData =
    stationUsage && stationUsage?.length > 0 && Array.isArray(stationUsage)
      ? stationUsage?.map((usage) => {
          const matchedStation = stationsData?.find(
            (s) => s?.stationDetails?.stationID === usage?.stationid
          );
          return {
            ...usage,
            stationName:
              matchedStation?.stationDetails?.name || usage?.stationid,
            address: matchedStation?.stationDetails?.address || "",
          };
        })
      : [];

  // Calculate dynamic y-axis max value
  const maxUsage =
    mergedStationData.length > 0
      ? Math.max(...mergedStationData.map((item) => item?.totalUsage || 0), 0)
      : 0;
  // Round up to nearest multiple of 4 to avoid decimal tick values
  const yAxisMax = maxUsage === 0 ? 100 : Math.ceil(maxUsage / 4) * 4;

  const chartOptions = {
    chart: {
      type: "bar",
      height: 400,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30%",
        borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories:
        mergedStationData.length > 0
          ? mergedStationData.map((item) => item.stationName)
          : [],
      labels: {
        rotate: -45,
        rotateAlways: true,
        style: {
          fontSize: "12px",
          fontWeight: 400,
          color: "#808080",
        },
      },
    },
    yaxis: [
      {
        title: {
          text: "Energy Units (Wh)",
          style: {
            fontSize: "12px",
            fontWeight: 400,
            color: "#808080",
          },
        },
        min: 0,
        max: yAxisMax,
        labels: {
          formatter: function (val) {
            return val.toFixed(0);
          },
        },
      },
    ],
    colors: ["#2E5B89"],
    fill: {
      opacity: 1,
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        const station = mergedStationData[dataPointIndex];
        return `
          <div style="padding:10px; font-size:13px;">
            <strong>${station?.stationName}</strong><br/>
            <span style="color:#808080">${station?.address}</span><br/>
            Energy: <strong>${series[seriesIndex][dataPointIndex]?.toFixed(
              2
            )} Wh</strong>
          </div>`;
      },
    },
    legend: {
      position: "bottom",
      showForSingleSeries: true,
      horizontalAlign: "center",
      offsetY: 5,
    },
    grid: {
      borderColor: "#f0f0f0",
      strokeDashArray: 3,
    },
  };

  const chartSeries = [
    {
      name: "Stations",

      data:
        mergedStationData.length > 0
          ? mergedStationData.map((item) => item.totalUsage)
          : [],
    },
  ];

  const handleDateRangeChange = (dateRangeData) => {
    const { startDate, endDate, type } = dateRangeData;
    const dateRange = {
      period: type,
      from: formatDateForPayload(startDate) || currentWeekRange.from,
      to: formatDateForPayload(endDate) || currentWeekRange.to,
    };
    setDateRange(dateRange);
  };

  useEffect(() => {
    if (dateRange) {
      dispatch(getAllStationUsage(setLoading, dateRange));
      dispatch(getAllStation(setLoading));
    }
  }, [dateRange]);

  return (
    <Container fluid className="pt-0">
      <Row>
        <Col>
          <Card style={{ border: "1px solid #0000001A", borderRadius: "10px" }}>
            <CardBody className="p-4">
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start mb-3 gap-3">
                <div>
                  <CardTitle
                    tag="h3"
                    className="mb-2"
                    style={{
                      fontSize: "18px",
                      color: "#0A0A0A",
                      fontWeight: "400",
                    }}
                  >
                    Charging Station Performance
                  </CardTitle>
                </div>
                <div className="flex-shrink-0">
                  <DateRangeSelector
                    onDateRangeChange={handleDateRangeChange}
                  />
                </div>
              </div>

              {mergedStationData.length > 0 ? (
                <Chart
                  options={chartOptions}
                  series={chartSeries}
                  type="bar"
                  height={300}
                />
              ) : (
                <div
                  style={{ height: "320px" }}
                  className="d-flex align-items-center justify-content-center"
                >
                  <img src={noData} width={320} alt="No Data" />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default StationUtilizationChart;
